/**
 * useOperatorsAdmin — аккаунты операторов для «Бот для клиентов» (OO/EO,
 * perm actor_approval). Список операторов + переключение авто-одобрения +
 * загрузка предпросмотра карточки «как сейчас».
 *
 * Backend:
 *  - GET GET /api/oo/operators                     → { count, operators:[Op] }
 *  - PUT /api/oo/operators/:id/auto-approve        body { autoApprove } → { ok, staffId, autoApprove }
 *  - GET /api/oo/operators/:id/profile-preview     → { staffId, nickname, profile, media:[MediaAsset], autoApprove, isDraft }
 *
 * auto-approve обновляется оптимистично (откат при ошибке).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export type CardStatus = "none" | "draft" | "published";

export interface OperatorAccount {
  staffId: string;
  nickname: string;
  /** Имя ПЕРСОНАЖА (напр. «Марси») — то, что видят клиенты. "" если карточки нет. */
  characterName: string;
  /** Роль персонажа (напр. «Жрица»). */
  displayRole: string;
  staffStatus: string;
  cardStatus: CardStatus;
  hasProfile: boolean;
  isPublic: boolean;
  autoApprove: boolean;
  hasPending: boolean;
  pendingCount: number;
  updatedAt: string | null;
}

/** Медиа-ассет карточки (как в операторском контракте). */
export interface MediaAsset {
  id: string;
  kind: "photo" | "video" | string;
  label: string;
  fileKey: string;
  thumbKey: string | null;
  width: number | null;
  height: number | null;
  fanOnly: boolean;
  filter: Record<string, unknown> | null;
  crops: Record<string, { x: number; y: number; w: number; h: number }> | null;
  streamUrl: string | null;
  posterUrl: string | null;
}

export interface OperatorProfilePreview {
  staffId: string;
  nickname: string;
  profile: Record<string, unknown>;
  media: MediaAsset[];
  autoApprove: boolean;
  isDraft: boolean;
}

interface OperatorsResponse {
  count?: number;
  operators?: OperatorAccount[];
}

export interface UseOperatorsAdminResult {
  operators: OperatorAccount[];
  count: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setAutoApprove: (staffId: string, autoApprove: boolean) => Promise<{ ok: boolean; error?: string }>;
  fetchPreview: (staffId: string) => Promise<OperatorProfilePreview | null>;
  saveProfile: (staffId: string, fields: Record<string, unknown>, publish: boolean) => Promise<{ ok: boolean; error?: string }>;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Ошибка";
}

export function useOperatorsAdmin(): UseOperatorsAdminResult {
  const [operators, setOperators] = useState<OperatorAccount[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<OperatorsResponse>("/api/oo/operators", { signal: ac.signal });
        if (ac.signal.aborted) return;
        const ops = (r.operators ?? []).slice().sort((a, b) => a.nickname.localeCompare(b.nickname, "ru"));
        setOperators(ops);
        setCount(r.count ?? ops.length);
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setOperators([]);
        setCount(0);
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger]);

  const setAutoApprove = useCallback(async (staffId: string, autoApprove: boolean) => {
    // optimistic
    setOperators((list) => list.map((o) => (o.staffId === staffId ? { ...o, autoApprove } : o)));
    try {
      await api(`/api/oo/operators/${staffId}/auto-approve`, { method: "PUT", body: { autoApprove } });
      return { ok: true };
    } catch (e: unknown) {
      // rollback
      setOperators((list) => list.map((o) => (o.staffId === staffId ? { ...o, autoApprove: !autoApprove } : o)));
      return { ok: false, error: errMsg(e) };
    }
  }, []);

  const fetchPreview = useCallback(async (staffId: string): Promise<OperatorProfilePreview | null> => {
    try {
      return await api<OperatorProfilePreview>(`/api/oo/operators/${staffId}/profile-preview`);
    } catch {
      return null;
    }
  }, []);

  const saveProfile = useCallback(async (staffId: string, fields: Record<string, unknown>, publish: boolean) => {
    try {
      await api(`/api/oo/operators/${staffId}/profile`, { method: "POST", body: { ...fields, publish } });
      reload(); // подтянуть свежий cardStatus/characterName
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, [reload]);

  return { operators, count, loading, error, reload, setAutoApprove, fetchPreview, saveProfile };
}
